/**
 * OTP Prompt — Promise-based 高危操作 OTP 弹层入口
 *
 * 设计：
 *  - axios 拦截器调用 `requestOtp()` → Promise<string>
 *  - `OtpPromptHost` 组件订阅 `subscribeOtpPrompt` 接收请求并展示 modal
 *  - 用户输入 → resolve(code)，取消 → reject(OtpPromptCancelledError)
 *  - 30s 内复用最近一次 OTP（同步内存缓存，避免连续操作重复输入）
 *
 * 缓存仅在内存中保留（页面刷新即失效），不写 localStorage，避免长期驻留高危凭证。
 */

export class OtpPromptCancelledError extends Error {
  constructor(message = "OTP cancelled by user") {
    super(message);
    this.name = "OtpPromptCancelledError";
  }
}

export interface OtpPromptRequest {
  /** UI 提示文案，可选 */
  title?: string;
  description?: string;
  /** 触发该请求的接口路径，仅用于展示 */
  endpoint?: string;
}

export interface OtpPromptHandle {
  request: OtpPromptRequest;
  resolve: (code: string) => void;
  reject: (err: Error) => void;
}

type Listener = (handle: OtpPromptHandle) => void;

const listeners: Set<Listener> = new Set();

export function subscribeOtpPrompt(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** OTP 短期内存缓存（默认 30s） */
const OTP_CACHE_TTL_MS = 30_000;
let cachedOtp: { code: string; expiresAt: number } | null = null;

export function getCachedOtp(): string | null {
  if (!cachedOtp) return null;
  if (Date.now() > cachedOtp.expiresAt) {
    cachedOtp = null;
    return null;
  }
  return cachedOtp.code;
}

export function setCachedOtp(code: string, ttlMs: number = OTP_CACHE_TTL_MS): void {
  cachedOtp = { code, expiresAt: Date.now() + ttlMs };
}

export function clearCachedOtp(): void {
  cachedOtp = null;
}

/**
 * 发起 OTP 输入请求。返回 Promise<otp_code>。
 * 优先返回缓存值；缓存命中时不会触发 modal。
 */
export function requestOtp(request: OtpPromptRequest = {}): Promise<string> {
  const cached = getCachedOtp();
  if (cached) {
    return Promise.resolve(cached);
  }

  if (listeners.size === 0) {
    return Promise.reject(
      new Error(
        "OtpPromptHost is not mounted. Make sure <OtpPromptHost /> is rendered in the app root."
      )
    );
  }

  return new Promise<string>((resolve, reject) => {
    const handle: OtpPromptHandle = {
      request,
      resolve: (code: string) => {
        setCachedOtp(code);
        resolve(code);
      },
      reject: (err: Error) => {
        reject(err);
      },
    };
    listeners.forEach((listener) => listener(handle));
  });
}
